import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import Sidebar from './Sidebar';
import Header from './Header';
import ConfirmationModal from './ConfirmationModal';

interface Institution {
  id: number;
  name: string;
  slug: string;
  description?: string;
  institution_code?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  principal_name?: string;
  principal_email?: string;
  organization_id: number;
  organization_name?: string;
  organization_slug?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
}

const Institutions: React.FC = () => {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const { isMobile } = useIsMobile();
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingInst, setEditingInst] = useState<Institution | null>(null);
  const [filters, setFilters] = useState({
    organizationId: '',
    search: '',
    status: 'active'
  });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    institution_code: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    principal_name: '',
    principal_email: '',
    organization_id: ''
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchInstitutions = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      if (filters.organizationId) {
        queryParams.append('organization_id', filters.organizationId);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/institutions?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstitutions(response.data.institutions || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load institutions');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/institutions/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data.organizations || []);
    } catch (err: any) {
      console.error('Failed to load organizations:', err.response?.data?.error || err.message);
    }
  }, [token]);

  useEffect(() => {
    fetchInstitutions();
    // Fetch organizations if user is global admin
    if (user?.user_role === 'global_admin') {
      fetchOrganizations();
    }
  }, [user, fetchInstitutions, fetchOrganizations]);

  const handleOpenDialog = (inst?: Institution) => {
    if (inst) {
      setEditingInst(inst);
      setFormData({
        name: inst.name,
        slug: inst.slug,
        description: inst.description || '',
        institution_code: inst.institution_code || '',
        address: inst.address || '',
        phone: inst.phone || '',
        email: inst.email || '',
        website: inst.website || '',
        principal_name: inst.principal_name || '',
        principal_email: inst.principal_email || '',
        organization_id: inst.organization_id?.toString() || ''
      });
    } else {
      setEditingInst(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        institution_code: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        principal_name: '',
        principal_email: '',
        organization_id: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingInst(null);
  };

  const handleSubmit = async () => {
    try {
      console.log('Submitting institution data:', formData);
      console.log('User role:', user?.user_role);
      console.log('Is editing:', !!editingInst);
      
      // Prepare submission data
      const submissionData: any = { ...formData };
      
      // For super admins creating new institutions, include organization_id
      if (!editingInst && user?.user_role === 'global_admin' && formData.organization_id) {
        submissionData.organization_id = parseInt(formData.organization_id);
        console.log('Added organization_id for super admin:', submissionData.organization_id);
      }
      
      console.log('Final submission data:', submissionData);
      
      if (editingInst) {
        const response = await axios.put(
          `${API_BASE_URL}/api/institutions/${editingInst.id}`,
          submissionData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Update response:', response.data);
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/institutions`,
          submissionData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Create response:', response.data);
      }
      handleCloseDialog();
      fetchInstitutions();
    } catch (err: any) {
      console.error('Institution creation error:', err);
      setError(err.response?.data?.error || 'Failed to save institution');
    }
  };

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });

  const handleDelete = (id: number) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.id) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/institutions/${deleteDialog.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInstitutions();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete institution');
    }
    setDeleteDialog({ open: false, id: null });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="content-area">
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h4" component="h1">
                <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('institutions.title')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {t('institutions.addInstitution')}
              </Button>
            </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters for Super Admins */}
      {user?.user_role === 'global_admin' && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('institutions.filters')}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Organization"
                value={filters.organizationId}
                onChange={(e) => setFilters({ ...filters, organizationId: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="">{t('institutions.allOrganizations')}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t('institutions.search')}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder={t('institutions.searchPlaceholder')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                select
                label={t('institutions.status')}
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="active">{t('institutions.active')}</option>
                <option value="inactive">{t('institutions.inactive')}</option>
                <option value="all">{t('institutions.all')}</option>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Button
                variant="outlined"
                onClick={() => setFilters({ organizationId: '', search: '', status: 'active' })}
                fullWidth
              >
                {t('institutions.clearFilters')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {institutions.map((inst) => (
            <Paper key={inst.id} sx={{ p: 2, borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box minWidth={0} flex={1}>
                  <Typography variant="subtitle1" fontWeight="bold">{inst.name}</Typography>
                  {inst.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{inst.description}</Typography>
                  )}
                </Box>
                <Chip
                  label={inst.is_active ? t('institutions.active') : t('institutions.inactive')}
                  color={inst.is_active ? 'success' : 'default'}
                  size="small"
                  sx={{ ml: 1, flexShrink: 0 }}
                />
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                {user?.user_role === 'global_admin' && inst.organization_name && (
                  <Chip label={inst.organization_name} size="small" variant="outlined" />
                )}
                {inst.institution_code && <Chip label={inst.institution_code} size="small" />}
              </Box>
              {(inst.principal_name || inst.email) && (
                <Box mb={1}>
                  {inst.principal_name && <Typography variant="caption" display="block">{inst.principal_name}</Typography>}
                  {inst.email && <Typography variant="caption" color="text.secondary" display="block">{inst.email}</Typography>}
                  {inst.phone && <Typography variant="caption" color="text.secondary" display="block">{inst.phone}</Typography>}
                </Box>
              )}
              <Box display="flex" justifyContent="flex-end" gap={0.5}>
                <IconButton size="small" onClick={() => handleOpenDialog(inst)} color="primary" aria-label="Edit institution"><EditIcon /></IconButton>
                <IconButton size="small" onClick={() => handleDelete(inst.id)} color="error" aria-label="Delete institution"><DeleteIcon /></IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('institutions.table.name')}</TableCell>
                {user?.user_role === 'global_admin' && <TableCell>{t('institutions.table.organization')}</TableCell>}
                <TableCell>{t('institutions.table.code')}</TableCell>
                <TableCell>{t('institutions.table.principal')}</TableCell>
                <TableCell>{t('institutions.table.contact')}</TableCell>
                <TableCell>{t('institutions.table.status')}</TableCell>
                <TableCell>{t('institutions.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {institutions.map((inst) => (
                <TableRow key={inst.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">{inst.name}</Typography>
                    {inst.description && <Typography variant="body2" color="text.secondary">{inst.description}</Typography>}
                  </TableCell>
                  {user?.user_role === 'global_admin' && (
                    <TableCell><Typography variant="body2">{inst.organization_name || 'Unknown Organization'}</Typography></TableCell>
                  )}
                  <TableCell>{inst.institution_code && <Chip label={inst.institution_code} size="small" />}</TableCell>
                  <TableCell>
                    {inst.principal_name && (
                      <>
                        <Typography variant="body2">{inst.principal_name}</Typography>
                        {inst.principal_email && <Typography variant="body2" color="text.secondary" fontSize="0.75rem">{inst.principal_email}</Typography>}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {inst.email && <Typography variant="body2">{inst.email}</Typography>}
                    {inst.phone && <Typography variant="body2" color="text.secondary">{inst.phone}</Typography>}
                  </TableCell>
                  <TableCell><Chip label={inst.is_active ? t('institutions.active') : t('institutions.inactive')} color={inst.is_active ? 'success' : 'default'} size="small" /></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDialog(inst)} color="primary" aria-label="Edit institution"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(inst.id)} color="error" aria-label="Delete institution"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingInst ? t('institutions.editInstitution') : t('institutions.addInstitution')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('institutions.form.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            
            {/* Organization selection for super admins */}
            {user?.user_role === 'global_admin' && !editingInst && (
              <TextField
                select
                label={t('institutions.organization')}
                value={formData.organization_id}
                onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                required
                fullWidth
                SelectProps={{
                  native: true,
                }}
                helperText="Select the organization this institution belongs to"
              >
                <option value="">Select an organization...</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </TextField>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('institutions.form.slug')}
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  required
                  fullWidth
                  helperText="URL-friendly identifier"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('institutions.form.institutionCode')}
                  value={formData.institution_code}
                  onChange={(e) => setFormData({ ...formData, institution_code: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label={t('institutions.form.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('institutions.form.principalName')}
                  value={formData.principal_name}
                  onChange={(e) => setFormData({ ...formData, principal_name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('institutions.form.principalEmail')}
                  type="email"
                  value={formData.principal_email}
                  onChange={(e) => setFormData({ ...formData, principal_email: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('institutions.form.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('institutions.form.phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label={t('institutions.form.website')}
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('institutions.form.address')}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('institutions.buttons.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingInst ? t('institutions.buttons.update') : t('institutions.buttons.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        isOpen={deleteDialog.open}
        title={t('institutions.deleteTitle', 'Delete Institution')}
        message={t('institutions.deleteConfirm', 'Are you sure you want to delete this institution? This action cannot be undone.')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ open: false, id: null })}
      />
          </Box>
        </div>
      </div>
    </div>
  );
};

export default Institutions;

